import { motion } from 'framer-motion';

// 5 Lore-accurate profile border animations + 1 Champion exclusive
export const PROFILE_BORDERS = [
  { id: 'toxic_pulse', name: 'Toxic Pulse', desc: 'Sewer radiation', unlock: 'free', color: '#22c55e', howTo: 'Free for all gremlins' },
  { id: 'gas_leak', name: 'Gas Leak', desc: 'Leaking fumes', unlock: 'games_100', color: '#a855f7', howTo: 'Play 100 arcade games' },
  { id: 'neon_trace', name: 'Neon Trace', desc: 'Arcade circuit', unlock: 'fees_100k', color: '#00ffb4', howTo: 'Steal 100K total fees' },
  { id: 'void_ring', name: 'Void Ring', desc: 'Dark rift', unlock: 'burn_10k', color: '#8b5cf6', howTo: 'Burn 10K $GFG tokens' },
  { id: 'gold_flame', name: 'Gold Flame', desc: 'Premium prestige', unlock: 'streak_14', color: '#ffd700', howTo: 'Reach a 14-day streak' },
  { id: 'champion_crown', name: 'Champion Crown', desc: 'Season champion', unlock: 'rank_gold', color: '#00b4ff', howTo: 'Reach Gold rank in a season' },
  { id: 'veil_walker', name: 'Veil Walker', desc: 'Ethereal mist from beyond', unlock: 'oracle_50w_75a', color: '#60a5fa', howTo: 'Oracle: 50 wins / 75% accuracy' },
  { id: 'fate_weaver', name: 'Fate Weaver', desc: 'Threads of fate bend to you', unlock: 'oracle_150w_85a_25s', color: '#c084fc', howTo: 'Oracle: 150 wins / 85% / 25 streak' },
  { id: 'the_anomaly', name: 'The Anomaly', desc: 'Reality fractures around you', unlock: 'oracle_500w_90a_50s', color: '#f472b6', howTo: 'Oracle: 500 wins / 90% / 50 streak' },
];

const ProfileBorder = ({ borderId = 'toxic_pulse', size = 120, children }) => {
  const s = size;
  const r = s / 2;

  return (
    <div className="relative" style={{ width: s, height: s }}>
      {/* Border effect layer */}
      <div className="absolute inset-0 rounded-full" style={{ zIndex: 0 }}>
        {borderId === 'toxic_pulse' && <ToxicPulse s={s} />}
        {borderId === 'gas_leak' && <GasLeak s={s} />}
        {borderId === 'neon_trace' && <NeonTrace s={s} r={r} />}
        {borderId === 'void_ring' && <VoidRing s={s} />}
        {borderId === 'gold_flame' && <GoldFlame s={s} />}
        {borderId === 'champion_crown' && <ChampionCrown s={s} />}
        {borderId === 'veil_walker' && <VeilWalker s={s} />}
        {borderId === 'fate_weaver' && <FateWeaver s={s} />}
        {borderId === 'the_anomaly' && <TheAnomaly s={s} />}
      </div>
      {/* Content sits on top */}
      <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
};

// 1. TOXIC PULSE - Refined sewer radiation with breathing emerald glow
const ToxicPulse = ({ s }) => {
  const r = s / 2;
  // Subtle orbiting toxic particles
  const particles = Array.from({ length: 6 }, (_, i) => ({
    angle: (i / 6) * 360,
    delay: i * 0.8,
    size: i % 2 === 0 ? 2.5 : 2,
  }));

  return (
    <>
      {/* Ambient radiation aura */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -10,
          background: 'radial-gradient(circle, rgba(34,197,94,0.06) 30%, transparent 70%)',
          filter: 'blur(8px)',
        }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Rotating conic gradient ring */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -4,
          background: 'conic-gradient(from 0deg, rgba(34,197,94,0.05), rgba(34,197,94,0.6), rgba(74,222,128,0.85), rgba(34,197,94,0.6), rgba(22,163,74,0.4), rgba(34,197,94,0.6), rgba(74,222,128,0.85), rgba(34,197,94,0.6), rgba(34,197,94,0.05))',
          padding: 2,
          WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
        }}
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
      />

      {/* Inner glow ring */}
      <div className="absolute rounded-full" style={{ inset: -1, border: '1px solid rgba(34,197,94,0.2)', boxShadow: 'inset 0 0 8px rgba(34,197,94,0.06)' }} />

      {/* Outer prestige ring */}
      <div className="absolute rounded-full" style={{ inset: -6, border: '1px solid rgba(34,197,94,0.1)' }} />

      {/* Breathing pulse glow */}
      <motion.div
        className="absolute rounded-full"
        style={{ inset: -3, border: '1.5px solid transparent' }}
        animate={{
          boxShadow: [
            '0 0 12px rgba(34,197,94,0.2), 0 0 25px rgba(34,197,94,0.06)',
            '0 0 22px rgba(34,197,94,0.4), 0 0 40px rgba(34,197,94,0.12)',
            '0 0 12px rgba(34,197,94,0.2), 0 0 25px rgba(34,197,94,0.06)',
          ],
        }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Orbiting toxic particles */}
      {particles.map((p, i) => (
        <motion.div
          key={`tp-${i}`}
          className="absolute"
          style={{ width: p.size, height: p.size, left: '50%', top: '50%', marginLeft: -p.size / 2, marginTop: -p.size / 2 }}
          animate={{ rotate: [p.angle, p.angle + 360] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'linear', delay: p.delay }}
        >
          <motion.div
            className="rounded-full"
            style={{
              width: p.size,
              height: p.size,
              background: i % 2 === 0 ? 'rgba(74,222,128,0.8)' : 'rgba(34,197,94,0.7)',
              boxShadow: `0 0 ${p.size * 3}px rgba(34,197,94,0.4)`,
              transform: `translateY(${-(r + 5)}px)`,
            }}
            animate={{ opacity: [0.2, 0.8, 0.2], scale: [0.7, 1.3, 0.7] }}
            transition={{ duration: 2, repeat: Infinity, delay: p.delay * 0.4, ease: 'easeInOut' }}
          />
        </motion.div>
      ))}
    </>
  );
};

// 2. GAS LEAK - Ethereal purple/green mist with drifting particles
const GasLeak = ({ s }) => {
  const r = s / 2;
  // Drifting gas particles
  const particles = Array.from({ length: 8 }, (_, i) => ({
    angle: (i / 8) * 360,
    delay: i * 0.4,
    color: i % 3 === 0 ? 'rgba(168,85,247,0.7)' : i % 3 === 1 ? 'rgba(34,197,94,0.6)' : 'rgba(139,92,246,0.65)',
    size: 2 + (i % 3),
  }));

  return (
    <>
      {/* Dual-tone ambient mist */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -12,
          background: 'radial-gradient(ellipse at 35% 40%, rgba(168,85,247,0.08) 0%, transparent 60%)',
          filter: 'blur(10px)',
        }}
        animate={{ rotate: [0, 180], scale: [1, 1.08, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ rotate: { duration: 20, repeat: Infinity, ease: 'linear' }, scale: { duration: 4, repeat: Infinity, ease: 'easeInOut' } }}
      />
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -12,
          background: 'radial-gradient(ellipse at 65% 60%, rgba(34,197,94,0.06) 0%, transparent 60%)',
          filter: 'blur(10px)',
        }}
        animate={{ rotate: [180, 0], opacity: [0.3, 0.6, 0.3] }}
        transition={{ rotate: { duration: 25, repeat: Infinity, ease: 'linear' }, opacity: { duration: 3.5, repeat: Infinity, ease: 'easeInOut' } }}
      />

      {/* Rotating dual-color conic gradient ring */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -4,
          background: 'conic-gradient(from 0deg, rgba(168,85,247,0.05), rgba(168,85,247,0.5), rgba(34,197,94,0.6), rgba(168,85,247,0.5), rgba(139,92,246,0.4), rgba(34,197,94,0.5), rgba(168,85,247,0.6), rgba(139,92,246,0.4), rgba(168,85,247,0.05))',
          padding: 2,
          WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
        }}
        animate={{ rotate: [0, -360] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
      />

      {/* Inner ring */}
      <div className="absolute rounded-full" style={{ inset: -1, border: '1px solid rgba(168,85,247,0.15)' }} />

      {/* Pulsing glow */}
      <motion.div
        className="absolute rounded-full"
        style={{ inset: -3, border: '1.5px solid transparent' }}
        animate={{
          boxShadow: [
            '0 0 10px rgba(168,85,247,0.15), 0 0 10px rgba(34,197,94,0.1)',
            '0 0 20px rgba(168,85,247,0.3), 0 0 20px rgba(34,197,94,0.2)',
            '0 0 10px rgba(168,85,247,0.15), 0 0 10px rgba(34,197,94,0.1)',
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Drifting gas particles — float upward elegantly */}
      {particles.map((p, i) => {
        const rad = (p.angle * Math.PI) / 180;
        const startX = Math.cos(rad) * (r + 4);
        const startY = Math.sin(rad) * (r + 4);
        return (
          <motion.div
            key={`gas-${i}`}
            className="absolute rounded-full"
            style={{
              width: p.size,
              height: p.size,
              backgroundColor: p.color,
              left: '50%',
              top: '50%',
              filter: 'blur(0.5px)',
              boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            }}
            animate={{
              x: [startX, startX + (Math.random() - 0.5) * 14],
              y: [startY, startY - 14 - Math.random() * 10],
              opacity: [0, 0.8, 0],
              scale: [0.6, 1.1, 0.3],
            }}
            transition={{
              duration: 2.5 + Math.random() * 1,
              repeat: Infinity,
              delay: p.delay,
              ease: 'easeOut',
            }}
          />
        );
      })}
    </>
  );
};

// 3. NEON TRACE - Dual-circuit arcade tracer with electric arcs and pulse nodes
const NeonTrace = ({ s, r }) => {
  // Pulse nodes at cardinal points
  const nodes = Array.from({ length: 4 }, (_, i) => {
    const angle = (i / 4) * Math.PI * 2 - Math.PI / 2;
    return {
      x: Math.cos(angle) * (r + 3),
      y: Math.sin(angle) * (r + 3),
      delay: i * 0.8,
    };
  });

  // Mini spark particles along the ring
  const sparks = Array.from({ length: 6 }, (_, i) => ({
    angle: (i / 6) * 360,
    delay: i * 0.6,
    size: i % 2 === 0 ? 2 : 1.5,
  }));

  return (
    <>
      {/* Ambient circuit glow */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -10,
          background: 'radial-gradient(circle, rgba(34,197,94,0.06) 30%, rgba(0,255,180,0.03) 60%, transparent 80%)',
          filter: 'blur(8px)',
        }}
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Rotating conic gradient ring — electric teal/green */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -4,
          background: 'conic-gradient(from 0deg, rgba(34,197,94,0.05), rgba(0,255,180,0.6), rgba(34,197,94,0.8), rgba(0,200,150,0.5), rgba(34,197,94,0.15), rgba(0,255,180,0.5), rgba(34,197,94,0.8), rgba(0,200,150,0.6), rgba(34,197,94,0.05))',
          padding: 2,
          WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
        }}
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
      />

      {/* Inner ring */}
      <div className="absolute rounded-full" style={{ inset: -1, border: '1px solid rgba(34,197,94,0.15)' }} />

      {/* Outer prestige ring */}
      <div className="absolute rounded-full" style={{ inset: -6, border: '1px solid rgba(34,197,94,0.08)' }} />

      {/* Primary tracer — bright green with long glow tail */}
      <motion.div
        className="absolute"
        style={{
          width: 7, height: 7, borderRadius: '50%',
          backgroundColor: '#00ffb4',
          boxShadow: '0 0 8px #00ffb4, 0 0 16px rgba(0,255,180,0.5), 0 0 30px rgba(34,197,94,0.25)',
          left: '50%', top: '50%', marginLeft: -3.5, marginTop: -3.5,
        }}
        animate={{
          x: Array.from({ length: 61 }, (_, i) => Math.cos((i / 60) * Math.PI * 2) * (r + 3)),
          y: Array.from({ length: 61 }, (_, i) => Math.sin((i / 60) * Math.PI * 2) * (r + 3)),
        }}
        transition={{ duration: 2.8, repeat: Infinity, ease: 'linear' }}
      />

      {/* Ghost trails — 3 fading echoes following the primary */}
      {[0.08, 0.16, 0.24].map((delay, idx) => (
        <motion.div
          key={`trail-${idx}`}
          className="absolute rounded-full"
          style={{
            width: 5 - idx, height: 5 - idx,
            backgroundColor: `rgba(0,255,180,${0.4 - idx * 0.12})`,
            boxShadow: `0 0 ${6 - idx * 2}px rgba(0,255,180,${0.3 - idx * 0.1})`,
            left: '50%', top: '50%', marginLeft: -(5 - idx) / 2, marginTop: -(5 - idx) / 2,
          }}
          animate={{
            x: Array.from({ length: 61 }, (_, i) => Math.cos((i / 60) * Math.PI * 2) * (r + 3)),
            y: Array.from({ length: 61 }, (_, i) => Math.sin((i / 60) * Math.PI * 2) * (r + 3)),
          }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'linear', delay }}
        />
      ))}

      {/* Secondary tracer — purple, opposite direction */}
      <motion.div
        className="absolute"
        style={{
          width: 5, height: 5, borderRadius: '50%',
          backgroundColor: '#a855f7',
          boxShadow: '0 0 8px #a855f7, 0 0 16px rgba(168,85,247,0.4)',
          left: '50%', top: '50%', marginLeft: -2.5, marginTop: -2.5,
        }}
        animate={{
          x: Array.from({ length: 61 }, (_, i) => Math.cos(Math.PI + (i / 60) * Math.PI * 2) * (r + 3)),
          y: Array.from({ length: 61 }, (_, i) => Math.sin(Math.PI + (i / 60) * Math.PI * 2) * (r + 3)),
        }}
        transition={{ duration: 3.8, repeat: Infinity, ease: 'linear' }}
      />

      {/* Purple ghost trail */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 3, height: 3,
          backgroundColor: 'rgba(168,85,247,0.3)',
          boxShadow: '0 0 4px rgba(168,85,247,0.2)',
          left: '50%', top: '50%', marginLeft: -1.5, marginTop: -1.5,
        }}
        animate={{
          x: Array.from({ length: 61 }, (_, i) => Math.cos(Math.PI + (i / 60) * Math.PI * 2) * (r + 3)),
          y: Array.from({ length: 61 }, (_, i) => Math.sin(Math.PI + (i / 60) * Math.PI * 2) * (r + 3)),
        }}
        transition={{ duration: 3.8, repeat: Infinity, ease: 'linear', delay: 0.1 }}
      />

      {/* Pulse nodes — flash when tracers pass through */}
      {nodes.map((node, i) => (
        <motion.div
          key={`node-${i}`}
          className="absolute rounded-full"
          style={{
            width: 4, height: 4,
            backgroundColor: 'rgba(0,255,180,0.6)',
            left: '50%', top: '50%',
            marginLeft: node.x - 2,
            marginTop: node.y - 2,
            boxShadow: '0 0 6px rgba(0,255,180,0.4)',
          }}
          animate={{
            scale: [1, 1.8, 1],
            opacity: [0.3, 1, 0.3],
            boxShadow: [
              '0 0 6px rgba(0,255,180,0.4)',
              '0 0 14px rgba(0,255,180,0.7), 0 0 25px rgba(0,255,180,0.3)',
              '0 0 6px rgba(0,255,180,0.4)',
            ],
          }}
          transition={{ duration: 2.8, repeat: Infinity, delay: i * 0.7, ease: 'easeInOut' }}
        />
      ))}

      {/* Orbiting micro sparks */}
      {sparks.map((sp, i) => (
        <motion.div
          key={`nsp-${i}`}
          className="absolute"
          style={{ width: sp.size, height: sp.size, left: '50%', top: '50%', marginLeft: -sp.size / 2, marginTop: -sp.size / 2 }}
          animate={{ rotate: [sp.angle, sp.angle - 360] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear', delay: sp.delay }}
        >
          <motion.div
            className="rounded-full"
            style={{
              width: sp.size, height: sp.size,
              background: i % 2 === 0 ? 'rgba(0,255,180,0.7)' : 'rgba(168,85,247,0.6)',
              boxShadow: `0 0 ${sp.size * 2}px ${i % 2 === 0 ? 'rgba(0,255,180,0.3)' : 'rgba(168,85,247,0.3)'}`,
              transform: `translateY(${-(r + 8)}px)`,
            }}
            animate={{ opacity: [0.1, 0.6, 0.1] }}
            transition={{ duration: 2, repeat: Infinity, delay: sp.delay * 0.3, ease: 'easeInOut' }}
          />
        </motion.div>
      ))}
    </>
  );
};

// 4. VOID RING - Deep space dimensional rift with galaxy vortex, stars and orbiting orbs
const VoidRing = ({ s }) => {
  const r = s / 2;
  // Orbiting void orbs
  const orbs = Array.from({ length: 4 }, (_, i) => ({
    offset: (i / 4) * Math.PI * 2,
    size: i % 2 === 0 ? 3.5 : 2.5,
    duration: 6 + i * 1.2,
  }));
  // Twinkling stars
  const stars = Array.from({ length: 10 }, (_, i) => {
    const angle = (i / 10) * Math.PI * 2;
    const dist = r + 6 + Math.random() * 14;
    return {
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist,
      size: Math.random() > 0.5 ? 2 : 1.5,
      delay: i * 0.3,
      duration: 2 + Math.random() * 2,
    };
  });

  return (
    <>
      {/* Galaxy dust — slow rotating nebula */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -14,
          background: 'radial-gradient(ellipse at 40% 45%, rgba(88,28,135,0.1) 0%, rgba(139,92,246,0.04) 40%, transparent 70%)',
          filter: 'blur(8px)',
        }}
        animate={{ rotate: [0, 360], scale: [1, 1.06, 1] }}
        transition={{ rotate: { duration: 30, repeat: Infinity, ease: 'linear' }, scale: { duration: 5, repeat: Infinity, ease: 'easeInOut' } }}
      />

      {/* Rotating conic gradient ring */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -4,
          background: 'conic-gradient(from 0deg, rgba(139,92,246,0.05), rgba(139,92,246,0.5), rgba(167,139,250,0.8), rgba(139,92,246,0.5), rgba(88,28,135,0.35), rgba(139,92,246,0.5), rgba(167,139,250,0.8), rgba(139,92,246,0.5), rgba(139,92,246,0.05))',
          padding: 2,
          WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
        }}
        animate={{ rotate: [0, -360] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
      />

      {/* Inner ring */}
      <div className="absolute rounded-full" style={{ inset: -1, border: '1px solid rgba(139,92,246,0.18)', boxShadow: 'inset 0 0 8px rgba(139,92,246,0.05)' }} />

      {/* Outer prestige ring */}
      <div className="absolute rounded-full" style={{ inset: -6, border: '1px solid rgba(139,92,246,0.08)' }} />

      {/* Pulsing void glow */}
      <motion.div
        className="absolute rounded-full"
        style={{ inset: -3, border: '1.5px solid transparent' }}
        animate={{
          boxShadow: [
            '0 0 12px rgba(139,92,246,0.2), 0 0 25px rgba(139,92,246,0.06)',
            '0 0 22px rgba(139,92,246,0.4), 0 0 40px rgba(139,92,246,0.12)',
            '0 0 12px rgba(139,92,246,0.2), 0 0 25px rgba(139,92,246,0.06)',
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Twinkling stars */}
      {stars.map((star, i) => (
        <motion.div
          key={`star-${i}`}
          className="absolute rounded-full"
          style={{
            width: star.size,
            height: star.size,
            backgroundColor: i % 3 === 0 ? 'rgba(199,170,255,0.85)' : i % 3 === 1 ? 'rgba(255,255,255,0.7)' : 'rgba(167,139,250,0.6)',
            left: '50%',
            top: '50%',
            marginLeft: star.x - star.size / 2,
            marginTop: star.y - star.size / 2,
            boxShadow: star.size > 1.8 ? '0 0 3px rgba(167,139,250,0.4)' : 'none',
          }}
          animate={{ opacity: [0.15, 0.9, 0.15], scale: [0.8, 1.2, 0.8] }}
          transition={{ duration: star.duration, repeat: Infinity, delay: star.delay, ease: 'easeInOut' }}
        />
      ))}

      {/* Orbiting void orbs */}
      {orbs.map((orb, i) => (
        <motion.div
          key={`orb-${i}`}
          className="absolute"
          style={{ width: orb.size, height: orb.size, left: '50%', top: '50%', marginLeft: -orb.size / 2, marginTop: -orb.size / 2 }}
          animate={{
            x: Array.from({ length: 61 }, (_, j) => Math.cos(orb.offset + (j / 60) * Math.PI * 2) * (r + 5)),
            y: Array.from({ length: 61 }, (_, j) => Math.sin(orb.offset + (j / 60) * Math.PI * 2) * (r + 5)),
          }}
          transition={{ duration: orb.duration, repeat: Infinity, ease: 'linear' }}
        >
          <motion.div
            className="rounded-full"
            style={{
              width: orb.size,
              height: orb.size,
              backgroundColor: 'rgba(139,92,246,0.8)',
              boxShadow: `0 0 6px rgba(139,92,246,0.5), 0 0 12px rgba(139,92,246,0.2)`,
            }}
            animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.2, 0.8] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>
      ))}
    </>
  );
};

// 5. GOLD FLAME - Intense golden fire with layered effects, licking flames, and molten core
const GoldFlame = ({ s }) => {
  const r = s / 2;
  
  // Elegant orbiting sparkles
  const sparkles = Array.from({ length: 8 }, (_, i) => ({
    angle: (i / 8) * 360,
    delay: i * 0.6,
    size: i % 2 === 0 ? 3 : 2,
  }));

  return (
    <>
      {/* Ambient golden aura */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -12,
          background: 'radial-gradient(circle, rgba(255,200,50,0.06) 30%, transparent 70%)',
          filter: 'blur(10px)',
        }}
        animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Rotating conic gradient ring — the premium core */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -4,
          background: 'conic-gradient(from 0deg, rgba(255,215,0,0.05), rgba(255,200,50,0.7), rgba(255,235,150,0.95), rgba(255,200,50,0.7), rgba(205,165,30,0.5), rgba(255,200,50,0.7), rgba(255,235,150,0.95), rgba(255,200,50,0.7), rgba(255,215,0,0.05))',
          padding: 2.5,
          WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 2.5px), black calc(100% - 2.5px))',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 2.5px), black calc(100% - 2.5px))',
        }}
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
      />

      {/* Static inner gold ring */}
      <div
        className="absolute rounded-full"
        style={{
          inset: -1,
          border: '1px solid rgba(255,215,0,0.25)',
          boxShadow: 'inset 0 0 10px rgba(255,200,50,0.08)',
        }}
      />

      {/* Outer prestige ring */}
      <div
        className="absolute rounded-full"
        style={{
          inset: -6,
          border: '1px solid rgba(255,215,0,0.12)',
        }}
      />

      {/* Pulsing glow ring */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -3,
          border: '1.5px solid transparent',
          boxShadow: '0 0 15px rgba(255,200,50,0.2), 0 0 30px rgba(255,180,0,0.08)',
        }}
        animate={{
          boxShadow: [
            '0 0 15px rgba(255,200,50,0.2), 0 0 30px rgba(255,180,0,0.08)',
            '0 0 25px rgba(255,215,0,0.35), 0 0 45px rgba(255,180,0,0.15)',
            '0 0 15px rgba(255,200,50,0.2), 0 0 30px rgba(255,180,0,0.08)',
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Shimmer sweep — rotating highlight */}
      <motion.div
        className="absolute rounded-full overflow-hidden"
        style={{ inset: -5 }}
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
      >
        <div
          className="absolute"
          style={{
            top: 0,
            left: '45%',
            width: '10%',
            height: '50%',
            background: 'linear-gradient(180deg, rgba(255,255,220,0.5) 0%, transparent 100%)',
            filter: 'blur(3px)',
            transformOrigin: 'center bottom',
          }}
        />
      </motion.div>

      {/* Orbiting sparkle particles — smooth and elegant */}
      {sparkles.map((sp, i) => (
        <motion.div
          key={`gsp-${i}`}
          className="absolute"
          style={{
            width: sp.size,
            height: sp.size,
            left: '50%',
            top: '50%',
            marginLeft: -sp.size / 2,
            marginTop: -sp.size / 2,
          }}
          animate={{
            rotate: [sp.angle, sp.angle + 360],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear',
            delay: sp.delay,
          }}
        >
          <motion.div
            className="rounded-full"
            style={{
              width: sp.size,
              height: sp.size,
              background: 'rgba(255,235,170,0.9)',
              boxShadow: `0 0 ${sp.size * 2}px rgba(255,215,0,0.5)`,
              transform: `translateY(${-(r + 5)}px)`,
            }}
            animate={{
              opacity: [0.2, 0.9, 0.2],
              scale: [0.7, 1.2, 0.7],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              delay: sp.delay * 0.5,
              ease: 'easeInOut',
            }}
          />
        </motion.div>
      ))}
    </>
  );
};

// 6. CHAMPION CROWN - Electric blue exclusive for #1 weekly champion
const ChampionCrown = ({ s }) => {
  const r = s / 2;

  // Electric arc segments
  const arcs = Array.from({ length: 6 }, (_, i) => ({
    angle: (i / 6) * 360,
    delay: i * 0.5,
    size: i % 2 === 0 ? 3 : 2.5,
  }));

  // Crown point sparkles (3 points like a crown)
  const crownPoints = Array.from({ length: 3 }, (_, i) => {
    const angle = ((i / 3) * Math.PI * 2) - Math.PI / 2; // top-centered
    return {
      x: Math.cos(angle) * (r + 8),
      y: Math.sin(angle) * (r + 8),
      delay: i * 0.4,
    };
  });

  return (
    <>
      {/* Deep blue ambient aura */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -14,
          background: 'radial-gradient(circle, rgba(0,120,255,0.08) 20%, rgba(0,180,255,0.04) 50%, transparent 75%)',
          filter: 'blur(10px)',
        }}
        animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Rotating conic gradient ring — electric blue/cyan */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -4,
          background: 'conic-gradient(from 0deg, rgba(0,120,255,0.05), rgba(0,150,255,0.6), rgba(0,200,255,0.9), rgba(100,200,255,0.7), rgba(0,120,255,0.4), rgba(0,150,255,0.6), rgba(0,200,255,0.9), rgba(100,200,255,0.7), rgba(0,120,255,0.05))',
          padding: 2.5,
          WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 2.5px), black calc(100% - 2.5px))',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 2.5px), black calc(100% - 2.5px))',
        }}
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
      />

      {/* Second counter-rotating ring for depth */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: -7,
          background: 'conic-gradient(from 180deg, rgba(0,200,255,0.02), rgba(0,150,255,0.2), rgba(0,200,255,0.02), rgba(0,150,255,0.2), rgba(0,200,255,0.02))',
          padding: 1,
          WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 1px), black calc(100% - 1px))',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 1px), black calc(100% - 1px))',
        }}
        animate={{ rotate: [360, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
      />

      {/* Inner glow ring */}
      <div className="absolute rounded-full" style={{ inset: -1, border: '1px solid rgba(0,180,255,0.2)', boxShadow: 'inset 0 0 10px rgba(0,150,255,0.06)' }} />

      {/* Pulsing champion glow */}
      <motion.div
        className="absolute rounded-full"
        style={{ inset: -3, border: '1.5px solid transparent' }}
        animate={{
          boxShadow: [
            '0 0 15px rgba(0,150,255,0.25), 0 0 30px rgba(0,120,255,0.08)',
            '0 0 30px rgba(0,180,255,0.5), 0 0 50px rgba(0,150,255,0.2), 0 0 70px rgba(0,120,255,0.08)',
            '0 0 15px rgba(0,150,255,0.25), 0 0 30px rgba(0,120,255,0.08)',
          ],
        }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Shimmer sweep */}
      <motion.div
        className="absolute rounded-full overflow-hidden"
        style={{ inset: -5 }}
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'linear' }}
      >
        <div
          className="absolute"
          style={{
            top: 0,
            left: '43%',
            width: '14%',
            height: '50%',
            background: 'linear-gradient(180deg, rgba(200,230,255,0.6) 0%, rgba(0,180,255,0.2) 40%, transparent 100%)',
            filter: 'blur(2px)',
            transformOrigin: 'center bottom',
          }}
        />
      </motion.div>

      {/* Crown point sparkles — 3 bright flashes at the top */}
      {crownPoints.map((cp, i) => (
        <motion.div
          key={`crown-${i}`}
          className="absolute"
          style={{
            width: 5, height: 5,
            left: '50%', top: '50%',
            marginLeft: cp.x - 2.5,
            marginTop: cp.y - 2.5,
          }}
        >
          <motion.div
            className="rounded-full"
            style={{
              width: 5, height: 5,
              backgroundColor: 'rgba(150,220,255,0.9)',
              boxShadow: '0 0 6px rgba(0,200,255,0.6), 0 0 14px rgba(0,150,255,0.3), 0 0 22px rgba(0,120,255,0.15)',
            }}
            animate={{
              scale: [1, 1.6, 1],
              opacity: [0.5, 1, 0.5],
              boxShadow: [
                '0 0 6px rgba(0,200,255,0.6), 0 0 14px rgba(0,150,255,0.3)',
                '0 0 12px rgba(0,220,255,0.9), 0 0 24px rgba(0,180,255,0.5), 0 0 36px rgba(0,150,255,0.2)',
                '0 0 6px rgba(0,200,255,0.6), 0 0 14px rgba(0,150,255,0.3)',
              ],
            }}
            transition={{ duration: 2, repeat: Infinity, delay: cp.delay, ease: 'easeInOut' }}
          />
        </motion.div>
      ))}

      {/* Orbiting electric arc particles */}
      {arcs.map((arc, i) => (
        <motion.div
          key={`arc-${i}`}
          className="absolute"
          style={{ width: arc.size, height: arc.size, left: '50%', top: '50%', marginLeft: -arc.size / 2, marginTop: -arc.size / 2 }}
          animate={{ rotate: [arc.angle, arc.angle + 360] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'linear', delay: arc.delay }}
        >
          <motion.div
            className="rounded-full"
            style={{
              width: arc.size, height: arc.size,
              background: i % 2 === 0 ? 'rgba(100,200,255,0.85)' : 'rgba(0,180,255,0.75)',
              boxShadow: `0 0 ${arc.size * 3}px rgba(0,180,255,0.4)`,
              transform: `translateY(${-(r + 5)}px)`,
            }}
            animate={{ opacity: [0.2, 0.9, 0.2], scale: [0.7, 1.3, 0.7] }}
            transition={{ duration: 2, repeat: Infinity, delay: arc.delay * 0.4, ease: 'easeInOut' }}
          />
        </motion.div>
      ))}
    </>
  );
};

// ============================================
// ORACLE EXCLUSIVE BORDERS
// ============================================

// TIER 1: Veil Walker — Ethereal misty ring with floating rune particles
const VeilWalker = ({ s }) => {
  const r = s / 2;
  const runeParticles = Array.from({ length: 14 }, (_, i) => ({
    angle: (i / 14) * 360,
    delay: i * 0.25,
    size: 3 + Math.random() * 2,
  }));
  return (
    <>
      {/* Outer mist ring — more vibrant */}
      <motion.div
        className="absolute rounded-full"
        style={{ width: s + 16, height: s + 16, left: -8, top: -8 }}
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: 'conic-gradient(from 0deg, rgba(96,165,250,0) 0%, rgba(96,165,250,0.5) 20%, rgba(139,92,246,0.4) 40%, rgba(96,165,250,0) 60%, rgba(139,92,246,0.45) 80%, rgba(96,165,250,0) 100%)',
          filter: 'blur(3px)',
        }} />
      </motion.div>
      {/* Bright inner glow ring */}
      <motion.div
        className="absolute rounded-full"
        style={{ width: s + 6, height: s + 6, left: -3, top: -3, border: '2px solid rgba(96,165,250,0.5)' }}
        animate={{
          opacity: [0.5, 1, 0.5],
          boxShadow: [
            '0 0 12px rgba(96,165,250,0.3), inset 0 0 8px rgba(96,165,250,0.15)',
            '0 0 25px rgba(96,165,250,0.6), inset 0 0 15px rgba(139,92,246,0.3)',
            '0 0 12px rgba(96,165,250,0.3), inset 0 0 8px rgba(96,165,250,0.15)',
          ]
        }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Floating rune particles orbiting */}
      {runeParticles.map((p, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ width: s, height: s, left: 0, top: 0 }}
          animate={{ rotate: [p.angle, p.angle + 360] }}
          transition={{ duration: 10 + i * 0.4, repeat: Infinity, ease: 'linear', delay: p.delay }}
        >
          <motion.div
            className="absolute rounded-full"
            style={{
              width: p.size + 2, height: p.size + 2,
              background: i % 3 === 0 ? '#60a5fa' : i % 3 === 1 ? '#818cf8' : '#a78bfa',
              boxShadow: `0 0 ${(p.size + 2) * 6}px ${i % 2 === 0 ? 'rgba(96,165,250,0.9)' : 'rgba(139,92,246,0.8)'}`,
              top: -6, left: '50%', marginLeft: -(p.size + 2) / 2,
            }}
            animate={{ opacity: [0.4, 1, 0.4], scale: [0.9, 1.5, 0.9] }}
            transition={{ duration: 1.8 + i * 0.15, repeat: Infinity, delay: p.delay * 0.5 }}
          />
        </motion.div>
      ))}
    </>
  );
};

// TIER 2: Fate Weaver — Swirling threads of fate with luminous trails
const FateWeaver = ({ s }) => {
  const threads = Array.from({ length: 6 }, (_, i) => ({
    id: i,
    duration: 5 + i * 1.2,
    delay: i * 0.6,
    offset: i * 60,
  }));
  return (
    <>
      {/* Base aura — much brighter */}
      <motion.div
        className="absolute rounded-full"
        style={{ width: s + 20, height: s + 20, left: -10, top: -10 }}
        animate={{ opacity: [0.25, 0.5, 0.25] }}
        transition={{ duration: 3, repeat: Infinity }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(circle, rgba(192,132,252,0.3) 40%, rgba(244,114,182,0.2) 65%, transparent 100%)',
          filter: 'blur(5px)',
        }} />
      </motion.div>
      {/* Spinning thread arcs — thicker, more vibrant */}
      {threads.map(t => (
        <motion.div
          key={t.id}
          className="absolute"
          style={{ width: s + 12, height: s + 12, left: -6, top: -6 }}
          animate={{ rotate: [t.offset, t.offset + 360] }}
          transition={{ duration: t.duration, repeat: Infinity, ease: 'linear', delay: t.delay }}
        >
          <svg width={s + 12} height={s + 12} viewBox={`0 0 ${s + 12} ${s + 12}`} className="absolute">
            <defs>
              <linearGradient id={`thread-${t.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(192,132,252,0)" />
                <stop offset="50%" stopColor={t.id % 2 === 0 ? 'rgba(192,132,252,1)' : 'rgba(244,114,182,0.9)'} />
                <stop offset="100%" stopColor="rgba(192,132,252,0)" />
              </linearGradient>
            </defs>
            <circle
              cx={(s + 12) / 2} cy={(s + 12) / 2} r={(s + 12) / 2 - 2}
              fill="none" stroke={`url(#thread-${t.id})`} strokeWidth="2"
              strokeDasharray={`${(s * Math.PI) / 4} ${(s * Math.PI) * 3 / 4}`}
              style={{ filter: `drop-shadow(0 0 6px ${t.id % 2 === 0 ? 'rgba(192,132,252,0.8)' : 'rgba(244,114,182,0.7)'})` }}
            />
          </svg>
        </motion.div>
      ))}
      {/* Center pulse ring — brighter */}
      <motion.div
        className="absolute rounded-full"
        style={{ width: s + 4, height: s + 4, left: -2, top: -2, border: '1.5px solid rgba(192,132,252,0.5)' }}
        animate={{ boxShadow: ['0 0 10px rgba(192,132,252,0.2)', '0 0 30px rgba(192,132,252,0.6), 0 0 50px rgba(244,114,182,0.2)', '0 0 10px rgba(192,132,252,0.2)'] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      />
      {/* Orbiting light nodes — bigger, brighter */}
      {[0, 90, 180, 270].map((angle, i) => (
        <motion.div
          key={`node-${i}`}
          className="absolute"
          style={{ width: s + 2, height: s + 2, left: -1, top: -1 }}
          animate={{ rotate: [angle, angle + 360] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'linear', delay: i * 0.4 }}
        >
          <motion.div
            className="absolute rounded-full"
            style={{
              width: 4, height: 4, top: -4, left: '50%', marginLeft: -2,
              background: i % 3 === 0 ? '#c084fc' : i % 3 === 1 ? '#f472b6' : '#818cf8',
              boxShadow: `0 0 16px ${i % 2 === 0 ? 'rgba(192,132,252,1)' : 'rgba(244,114,182,0.9)'}`,
            }}
            animate={{ scale: [1, 1.8, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.25 }}
          />
        </motion.div>
      ))}
    </>
  );
};

// TIER 3: The Anomaly — Reality-bending distortion with dimensional cracks
const TheAnomaly = ({ s }) => {
  const cracks = Array.from({ length: 10 }, (_, i) => ({
    id: i,
    angle: (i / 10) * 360 + Math.random() * 15 - 7,
    length: 10 + Math.random() * 14,
    delay: i * 0.15,
  }));
  return (
    <>
      {/* Distortion field — brighter, wider */}
      <motion.div
        className="absolute rounded-full"
        style={{ width: s + 24, height: s + 24, left: -12, top: -12 }}
        animate={{ scale: [1, 1.04, 0.97, 1], opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 1.8, repeat: Infinity }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(circle, transparent 35%, rgba(244,114,182,0.2) 50%, rgba(139,92,246,0.25) 70%, rgba(96,165,250,0.15) 90%, transparent 100%)',
          filter: 'blur(3px)',
        }} />
      </motion.div>
      {/* Glitching border — chromatic aberration — thicker, brighter */}
      <motion.div
        className="absolute rounded-full"
        style={{ width: s + 6, height: s + 6, left: -3, top: -3, border: '2px solid rgba(244,114,182,0.7)' }}
        animate={{ x: [0, 2, -2, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 0.12, repeat: Infinity, repeatDelay: 1.5 }}
      />
      <motion.div
        className="absolute rounded-full"
        style={{ width: s + 6, height: s + 6, left: -3, top: -3, border: '2px solid rgba(96,165,250,0.6)' }}
        animate={{ x: [0, -2, 2, 0], opacity: [0.4, 0.9, 0.4] }}
        transition={{ duration: 0.12, repeat: Infinity, repeatDelay: 1.5, delay: 0.04 }}
      />
      <motion.div
        className="absolute rounded-full"
        style={{ width: s + 6, height: s + 6, left: -3, top: -3, border: '2px solid rgba(192,132,252,0.7)' }}
        animate={{ y: [0, 2, -2, 0], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 0.12, repeat: Infinity, repeatDelay: 1.5, delay: 0.08 }}
      />
      {/* Dimensional cracks — brighter, thicker */}
      <svg
        className="absolute"
        style={{ width: s + 34, height: s + 34, left: -17, top: -17 }}
        viewBox={`0 0 ${s + 34} ${s + 34}`}
      >
        {cracks.map(crack => {
          const cx = (s + 34) / 2;
          const cy = (s + 34) / 2;
          const rad = (crack.angle * Math.PI) / 180;
          const startR = s / 2 + 2;
          const endR = startR + crack.length;
          const x1 = cx + Math.cos(rad) * startR;
          const y1 = cy + Math.sin(rad) * startR;
          const x2 = cx + Math.cos(rad) * endR;
          const y2 = cy + Math.sin(rad) * endR;
          return (
            <motion.line
              key={crack.id}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={crack.id % 3 === 0 ? 'rgba(244,114,182,0.9)' : crack.id % 3 === 1 ? 'rgba(192,132,252,0.8)' : 'rgba(96,165,250,0.8)'}
              strokeWidth="1.5"
              style={{ filter: `drop-shadow(0 0 5px ${crack.id % 3 === 0 ? 'rgba(244,114,182,0.6)' : 'rgba(192,132,252,0.5)'})` }}
              animate={{ opacity: [0, 1, 0], pathLength: [0, 1, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: crack.delay + 0.5, repeatDelay: 2 }}
            />
          );
        })}
      </svg>
      {/* Central anomaly pulse — much brighter */}
      <motion.div
        className="absolute rounded-full"
        style={{ width: s + 12, height: s + 12, left: -6, top: -6 }}
        animate={{
          boxShadow: [
            '0 0 12px rgba(244,114,182,0.15), inset 0 0 12px rgba(192,132,252,0.1)',
            '0 0 35px rgba(244,114,182,0.45), 0 0 60px rgba(192,132,252,0.2), inset 0 0 20px rgba(192,132,252,0.25)',
            '0 0 12px rgba(244,114,182,0.15), inset 0 0 12px rgba(192,132,252,0.1)',
          ]
        }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      {/* Orbiting anomaly fragments — bigger, brighter */}
      {[0, 72, 144, 216, 288].map((angle, i) => (
        <motion.div
          key={`frag-${i}`}
          className="absolute"
          style={{ width: s + 6, height: s + 6, left: -3, top: -3 }}
          animate={{ rotate: [angle, angle + (i % 2 === 0 ? 360 : -360)] }}
          transition={{ duration: 8 + i * 1.5, repeat: Infinity, ease: 'linear' }}
        >
          <motion.div
            className="absolute"
            style={{
              width: 3, height: 8, top: -5, left: '50%', marginLeft: -1.5,
              background: i % 3 === 0 ? '#f472b6' : i % 3 === 1 ? '#c084fc' : '#60a5fa',
              borderRadius: 1.5,
              boxShadow: `0 0 12px ${i % 2 === 0 ? 'rgba(244,114,182,0.9)' : 'rgba(192,132,252,0.8)'}`,
            }}
            animate={{ opacity: [0.4, 1, 0.4], scaleY: [0.6, 1.6, 0.6] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.3 }}
          />
        </motion.div>
      ))}
    </>
  );
};

export default ProfileBorder;
